import Logger from "../../../core/internal/logger"
import { InAppMessageDeliverResponse } from "./InAppMessageDeliverResponse"
import { InAppMessageDeliverRequest } from "./InAppMessageDeliverRequest"
import { InAppMessageDeliverEvaluator } from "./evaluator/InAppMessageDeliverEvaluator"
import { InAppMessagePresentRequest } from "../present/InAppMessagePresentRequest"
import { InAppMessagePresentProcessor } from "../present/InAppMessagePresentProcessor"
import { UserManager } from "../../../core/internal/user/UserManager"
import { InAppMessageIdentifierChecker } from "../evaluation/InAppMessageIdentifierChecker"
import { UserDecorator } from "../../../core/internal/user/UserDecorator"

export class InAppMessageDeliverProcessor {
  constructor(
    private readonly userManager: UserManager,
    private readonly identifierChecker: InAppMessageIdentifierChecker,
    private readonly evaluator: InAppMessageDeliverEvaluator,
    private readonly presentProcessor: InAppMessagePresentProcessor,
    private readonly userDecorator: UserDecorator
  ) {}

  private async response(request: InAppMessageDeliverRequest): Promise<InAppMessageDeliverResponse> {
    try {
      return await this.deliver(request)
    } catch (err) {
      Logger.log.error(`Failed to process InAppMessageDeliver: ${err}`)
      return InAppMessageDeliverResponse.of(request, "EXCEPTION")
    }
  }

  async process(request: InAppMessageDeliverRequest): Promise<InAppMessageDeliverResponse> {
    Logger.log.debug(`InAppMessage Deliver Request: ${request}`)
    const response = await this.response(request)
    Logger.log.debug(`InAppMessage Deliver Response: ${response}`)
    return response
  }

  private async deliver(request: InAppMessageDeliverRequest): Promise<InAppMessageDeliverResponse> {
    // check User
    const user = this.userDecorator.decorate(this.userManager.hackleUser())
    if (this.identifierChecker.isIdentifierChanged(request.identifiers, user.identifiers)) {
      return InAppMessageDeliverResponse.of(request, "IDENTIFIER_CHANGED")
    }

    // evaluate (re-eval + dedup)
    const response = await this.evaluator.evaluate(request, user)
    if (!response.isEligible) {
      return InAppMessageDeliverResponse.of(request, response.code ?? "INELIGIBLE")
    }

    // present
    const evaluation = response.evaluation!
    const presentRequest = InAppMessagePresentRequest.of(request, user, evaluation)
    const presentResponse = await this.presentProcessor.process(presentRequest)

    return InAppMessageDeliverResponse.of(request, "DELIVER", presentResponse)
  }
}
